/**
 * O protocolo serial do robô, sem porta nenhuma — funções puras, testáveis sem
 * navegador. A fonte de verdade é o firmware:
 * `devices/robo/AmazonasInnovare/DirectConsole.h` (`processProvisioning`) e
 * `DeviceInfo.h` (`montarInfo`). Cada limite aqui repete um limite de lá, e o
 * comentário diz qual.
 */

/**
 * O que o console devolve em `POST .../provisionamento`. `tenant_id` só vem no
 * robô legado (a Ivy), cujo firmware pode ser o antigo e exigir o campo; o
 * robô da plataforma não tem tenant (spec 2026-09-24, D1).
 */
export type ConfigDoConsole = {
  token: string
  /** Só no console anterior à revisão R2, que mandava o caminho antigo no topo. */
  tenant_id?: number
  device_id: string
  snapshot_path: string
  /**
   * Revisão R2 (spec 2026-09-24, 4.6): o caminho antigo, com tenant, para o
   * firmware anterior que recusa o `CONFIG` sem tenant. Usa o MESMO token.
   */
  legado?: { tenant_id: number; snapshot_path: string }
  /** A rede Wi-Fi padrão, quando pedida junto (spec 2026-09-29-robo-wifi-padrao, 4.5). */
  wifi_padrao?: { ssid: string; senha: string }
}

export type Wifi = { ssid: string; password: string }

/** A resposta ao `INFO` (firmware com `DeviceInfo.h`). */
export type InfoDoRobo = {
  perfil: string
  versao: string
  mac: string
  configurado: boolean
  app: string
}

export const LINHA_INFO = 'INFO'

/**
 * `ETAPA <nome>` (spec 2026-09-24-robo-painel-de-ajustes, 4.5): diz ao robô em que
 * passo da configuração a tela está, e ele encena o passo. Resposta `ETAPA_OK`;
 * firmware anterior responde `CONFIG_INVALIDA`, e a tela ignora e segue.
 */
export type NomeDaEtapa = 'identificar' | 'credencial' | 'gravar' | 'fim'
export const RESPOSTA_ETAPA = 'ETAPA_OK'
export const linhaEtapa = (nome: NomeDaEtapa) => `ETAPA ${nome}`

const bytes = (s: string) => new TextEncoder().encode(s).length

/**
 * O firmware mede em BYTES (`String::length()` do Arduino): SSID de 1 a 32,
 * senha até 63. Um "ç" conta dois — validar por caracteres deixaria passar
 * uma senha que o robô recusa com CONFIG_INVALIDA, depois de a credencial
 * antiga já ter sido invalidada.
 */
export function problemaNoWifi(w: Wifi): string | null {
  if (bytes(w.ssid) === 0) return 'Informe o nome da rede.'
  if (bytes(w.ssid) > 32) return 'O nome da rede passa de 32 bytes, e o robô não aceita.'
  if (bytes(w.password) > 63) return 'A senha passa de 63 bytes, e o robô não aceita.'
  if (/[\r\n]/.test(w.ssid) || /[\r\n]/.test(w.password)) return 'Rede e senha não podem ter quebra de linha.'
  return null
}

/**
 * A linha `CONFIG {...}`. Uma linha só: o firmware lê até o `\n`. Sem
 * `tenant_id` do console, o campo não vai — nem 0 nem null, que afirmariam um
 * cliente que o robô não tem.
 */
export function linhaConfig(c: ConfigDoConsole, w: Wifi): string {
  return 'CONFIG ' + JSON.stringify({
    token: c.token,
    ...(c.tenant_id != null ? { tenant_id: c.tenant_id } : {}),
    device_id: c.device_id,
    snapshot_path: c.snapshot_path,
    ssid: w.ssid,
    password: w.password,
  })
}

export function interpretarInfo(linha: string): InfoDoRobo | null {
  if (!linha.startsWith('INFO ')) return null
  try {
    const d = JSON.parse(linha.slice(5)) as Partial<InfoDoRobo>
    if (typeof d.perfil !== 'string' || typeof d.versao !== 'string'
      || typeof d.mac !== 'string' || typeof d.configurado !== 'boolean'
      || typeof d.app !== 'string') return null
    return d as InfoDoRobo
  } catch {
    return null
  }
}

/** O que uma linha do robô significa para o acompanhamento depois do CONFIG. */
export type Sinal =
  | { tipo: 'boot' }
  | { tipo: 'wifi' }
  | { tipo: 'hora' }
  | { tipo: 'console'; status: number }
  | { tipo: 'outro' }

/**
 * `LIVE: Wi-Fi conectado.` e `LIVE: hora ajustada.` só existem a partir da
 * TASK-11350; `LIVE: WiFi OK; HTTPS GET status=N` existe desde sempre, e ela
 * sozinha já prova Wi-Fi e hora (o firmware só faz o GET com os dois).
 */
export function interpretarLinha(linha: string): Sinal {
  if (linha.startsWith('LIVE: Wi-Fi conectado')) return { tipo: 'wifi' }
  if (linha.startsWith('LIVE: hora ajustada')) return { tipo: 'hora' }
  const m = /^LIVE: WiFi OK; HTTPS GET status=(-?\d+)/.exec(linha)
  if (m) return { tipo: 'console', status: Number(m[1]) }
  if (linha.startsWith('LIVE:')) return { tipo: 'boot' }
  return { tipo: 'outro' }
}

/**
 * Redes pela USB (spec 2026-09-24-robo-wifi-pela-frota-e-pela-usb, 4.1). O
 * firmware responde no mesmo `processProvisioning`; o que não tem estes
 * comandos responde `CONFIG_INVALIDA`, e é assim que a tela os detecta.
 */
export type RedeSalva = { ssid: string; confirmada: boolean; em_uso: boolean }
export type RedeVista = { ssid: string; rssi: number; auth: string }

export const LINHA_WIFI_LIST = 'WIFI_LIST'
export const LINHA_WIFI_SCAN = 'WIFI_SCAN'

/** `WIFI_DEL {"ssid":...}`. Mesmos limites de SSID do `CONFIG`. */
export function linhaWifiDel(ssid: string): string {
  return 'WIFI_DEL ' + JSON.stringify({ ssid })
}

export function problemaNoSsid(ssid: string): string | null {
  return problemaNoWifi({ ssid, password: '' })
}

function redesDaLinha(linha: string, prefixo: string): unknown[] | null {
  if (!linha.startsWith(prefixo + ' ')) return null
  try {
    const d = JSON.parse(linha.slice(prefixo.length + 1)) as { redes?: unknown }
    return Array.isArray(d.redes) ? d.redes : null
  } catch {
    return null
  }
}

/** `WIFI_LIST {"redes":[{"ssid","confirmada","em_uso"}]}`. Item malformado invalida a linha. */
export function interpretarWifiList(linha: string): RedeSalva[] | null {
  const redes = redesDaLinha(linha, LINHA_WIFI_LIST)
  if (!redes) return null
  const ok = redes.every((r) => {
    const x = r as Partial<RedeSalva> | null
    return !!x && typeof x.ssid === 'string' && typeof x.confirmada === 'boolean'
      && typeof x.em_uso === 'boolean'
  })
  return ok ? (redes as RedeSalva[]).map(({ ssid, confirmada, em_uso }) => ({ ssid, confirmada, em_uso })) : null
}

/** `WIFI_SCAN {"redes":[{"ssid","rssi","auth"}]}`, já filtrado pelo firmware. */
export function interpretarWifiScan(linha: string): RedeVista[] | null {
  const redes = redesDaLinha(linha, LINHA_WIFI_SCAN)
  if (!redes) return null
  const ok = redes.every((r) => {
    const x = r as Partial<RedeVista> | null
    return !!x && typeof x.ssid === 'string' && typeof x.rssi === 'number' && typeof x.auth === 'string'
  })
  return ok ? (redes as RedeVista[]).map(({ ssid, rssi, auth }) => ({ ssid, rssi, auth })) : null
}

/** A resposta ao `WIFI_DEL`. `undefined` = a linha não é resposta a ele. */
export type RespostaWifiDel = 'removida' | 'ultima' | 'recusada'

export function interpretarWifiDel(linha: string): RespostaWifiDel | undefined {
  if (linha === 'WIFI_DEL_OK') return 'removida'
  if (linha === 'WIFI_ULTIMA') return 'ultima'
  if (linha === 'CONFIG_INVALIDA') return 'recusada'
  return undefined
}

/**
 * Ajustes pela USB (spec 2026-09-24-robo-painel-de-ajustes, 5.2). O contrato é o
 * do firmware (`DirectConsole.h`, ramo `AJUSTE `, e `AjustesTela.h`), não o da
 * spec, onde os dois divergem:
 *
 * - o `AJUSTE` leva o estado desejado INTEIRO, o mesmo bloco que o snapshot manda:
 *   campo ausente volta ao padrão (`aplicarAjustes`). Por isso a linha sai do
 *   `desejado` que o servidor devolveu, e não só do campo que mudou;
 * - `versao` tem de ser maior que zero: zero responde `AJUSTE_INVALIDO`;
 * - a resposta é `AJUSTE_OK <cabecalho>`, o mesmo texto de `X-Robo-Ajustes`
 *   (`N` ou `N;recusado=<campos>`, com brilho, rotacao e rotacao_info), e ela traz
 *   a versão que o robô TEM,
 *   que só é a enviada se ele aplicou;
 * - firmware sem o comando responde `CONFIG_INVALIDA`.
 */
export type BlocoDeAjuste =
  | { versao: number; brilho?: number; rotacao?: number; rotacao_info?: number; reinicio?: number }
  | { versao: number; padrao: true }

export const LINHA_DIAG = 'DIAG'
export const LINHA_REINICIAR = 'REINICIAR'

/** Uma linha só; chave nula ou ausente não vai, como no bloco do snapshot (`robo_routes.py`). */
export function linhaAjuste(obj: BlocoDeAjuste): string {
  const limpo = Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== null && v !== undefined))
  return 'AJUSTE ' + JSON.stringify(limpo)
}

const CAMPOS_RECUSAVEIS = new Set(['brilho', 'rotacao', 'rotacao_info'])

/** O texto de `montarCabecalhoAjustes` (`AjustesPolicy.h`): `N` ou `N;recusado=a[,b]`. */
export function lerCabecalhoDeAjustes(texto: string): { versao: number; recusado: string[] } | null {
  // Até 3 campos, com `_` (`rotacao_info`, TASK-24847): o nome fora da lista ainda é
  // recusado logo abaixo.
  const m = /^(\d{1,10})(?:;recusado=([a-z_]+(?:,[a-z_]+){0,2}))?$/.exec(texto)
  if (!m) return null
  const recusado = m[2] ? m[2].split(',') : []
  if (!recusado.every((c) => CAMPOS_RECUSAVEIS.has(c))) return null
  return { versao: Number(m[1]), recusado }
}

export function lerAjusteOk(linha: string): { versao: number; recusado: string[] } | null {
  if (!linha.startsWith('AJUSTE_OK ')) return null
  return lerCabecalhoDeAjustes(linha.slice(10))
}

/** `DIAG {...}` (`DirectConsole.h`). `rssi` e `ssid` só com o Wi-Fi conectado. */
export type Diag = {
  uptime_s: number
  reset: string
  heap: number
  pilha: number
  rssi?: number
  ssid?: string
  /** A versão de ajustes que o robô tem guardada. */
  ajustes?: number
}

export function lerDiag(linha: string): Diag | null {
  if (!linha.startsWith('DIAG ')) return null
  try {
    const d = JSON.parse(linha.slice(5)) as Partial<Diag> | null
    if (!d || typeof d.uptime_s !== 'number' || typeof d.reset !== 'string'
      || typeof d.heap !== 'number' || typeof d.pilha !== 'number') return null
    if (d.rssi !== undefined && typeof d.rssi !== 'number') return null
    if (d.ssid !== undefined && typeof d.ssid !== 'string') return null
    if (d.ajustes !== undefined && typeof d.ajustes !== 'number') return null
    const { uptime_s, reset, heap, pilha, rssi, ssid, ajustes } = d
    return {
      uptime_s, reset, heap, pilha,
      ...(rssi !== undefined ? { rssi } : {}),
      ...(ssid !== undefined ? { ssid } : {}),
      ...(ajustes !== undefined ? { ajustes } : {}),
    }
  } catch {
    return null
  }
}

/** `RESOURCES: sample_ms=.. free_heap_bytes=.. network_stack_min_bytes=..` (`InteractionTransport.h`). */
export type Recursos = { heap: number; pilhaRede: number }

export function lerRecursos(linha: string): Recursos | null {
  const m = /^RESOURCES: sample_ms=\d+ free_heap_bytes=(\d+) network_stack_min_bytes=(\d+)/.exec(linha)
  return m ? { heap: Number(m[1]), pilhaRede: Number(m[2]) } : null
}

/** A resposta ao `AJUSTE`. `undefined` = a linha não é resposta a ele. */
export type RespostaAjuste =
  | { tipo: 'ok'; versao: number; recusado: string[] }
  | { tipo: 'invalido' }
  | { tipo: 'sem_comando' }

export function interpretarRespostaAjuste(linha: string): RespostaAjuste | undefined {
  const ok = lerAjusteOk(linha)
  if (ok) return { tipo: 'ok', ...ok }
  if (linha === 'AJUSTE_INVALIDO') return { tipo: 'invalido' }
  if (linha === 'CONFIG_INVALIDA') return { tipo: 'sem_comando' }
  return undefined
}
